import { useEffect, useState } from 'react';
import { Loader2, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface OfflineNoticeProps {
  /** Override le détecteur natif `navigator.onLine`. */
  online?: boolean;
  /** Texte du message offline. */
  offlineLabel?: string;
  /** Texte du message reconnexion. */
  reconnectingLabel?: string;
  /** Position fixed bottom-right (défaut) ou inline static. */
  position?: 'fixed' | 'inline';
  className?: string;
}

/**
 * Notice de statut offline standardisée — fixed bottom-right par défaut.
 * Utilise `navigator.onLine` + listeners `online`/`offline` ; la prop `online`
 * permet d'override (utile pour les tests ou un signal de sync custom).
 *
 * @example
 *   <OfflineNotice />
 *   <OfflineNotice position="inline" online={isOnline} />
 *
 * Remplace `OfflineBanner.tsx` (neobrutaliste) ; light + dark aware,
 * `role="status"` + `aria-live="polite"` pour annonce assistive.
 */
export function OfflineNotice({
  online: onlineProp,
  offlineLabel = 'Hors ligne',
  reconnectingLabel = 'Reconnexion…',
  position = 'fixed',
  className,
}: OfflineNoticeProps) {
  const [nativeOnline, setNativeOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [reconnecting, setReconnecting] = useState(false);
  const [visible, setVisible] = useState(false);

  const online = onlineProp ?? nativeOnline;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      setReconnecting(true);
      setNativeOnline(true);
      const t = setTimeout(() => {
        setReconnecting(false);
        setVisible(false);
      }, 1000);
      return () => clearTimeout(t);
    };
    const handleOffline = () => {
      setNativeOnline(false);
      setVisible(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    if (!navigator.onLine) setVisible(true);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Si la prop `online` est fournie, elle pilote la visibilité directement.
  const shouldShow = onlineProp !== undefined ? !online : visible;
  if (!shouldShow) return null;

  const positionCls = position === 'fixed' ? 'fixed bottom-4 right-4 z-30' : 'inline-flex';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      data-floating-dock={position === 'fixed' ? 'offline' : undefined}
      className={cn(
        'flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/95 px-3.5 py-2 text-xs font-medium text-white shadow-[0_8px_24px_-8px_rgba(15,23,42,0.2)] backdrop-blur-sm transition-all duration-300',
        positionCls,
        className,
      )}
    >
      {reconnecting ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <WifiOff className="h-3.5 w-3.5" aria-hidden />
      )}
      <span>{reconnecting ? reconnectingLabel : offlineLabel}</span>
    </div>
  );
}
